/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FiAlertTriangle, FiCheck, FiInfo } from 'react-icons/fi'
import { Link, useBlocker, useNavigate } from 'react-router-dom'

import { Button, ConfirmModal, Modal, Toast } from '@/components/ui'
import { AddressAutocomplete } from '@/components/ui/address-autocomplete'
import { useApi, useToast } from '@/hooks'

import { GeofenceDrawMap } from './GeofenceDrawMap'
import { GeofenceForm } from './GeofenceForm'
import { ShapeToolbar } from './ShapeToolbar'

import { createGeofence, updateGeofence, writeFailure, type WriteFailure } from '../api'
import { GeofencePageHeader } from '../components/GeofencePageHeader'
import { areaSquareMeters, formatArea, pointsFromShape, ringVertices, shapeFromPoints } from '../geometry'
import { useInvalidateGeofences } from '../hooks'
import {
  COMPLEXITY_TYPE_IDS,
  type GeofenceCategory,
  type GeofenceDetail,
  type GeofenceFormValues,
  type GeofenceShape,
  type ManagedGeofence,
  SHAPE_LABELS,
  type ShapeType,
  shapeTypeOf,
} from '../types'
import { isLargeShape, validateGeofenceName, validateShape } from '../validation'

const DEFAULT_VALUES: GeofenceFormValues = { name: '', categoryIds: [], zorder: 2, visible: true }

const DRAW_TIPS: Record<ShapeType, string> = {
  polygon: 'Click on the map to add points. Click the first point, or press Enter, to finish.',
  rectangle: 'Click and drag, or click two opposite corners, to draw a rectangle.',
  circle: 'Click the centre, then drag or click again to set the radius.',
}

const EDIT_TIPS: Record<ShapeType, string> = {
  polygon:
    'Drag a point to move it, drag a midpoint to add one, right-click a point to remove it. Drag inside to move the shape.',
  rectangle: 'Drag a corner to resize the rectangle, or drag inside to move it.',
  circle: 'Drag the edge to resize the circle, or drag inside to move it.',
}

const sameValues = (a: GeofenceFormValues, b: GeofenceFormValues) =>
  a.name === b.name &&
  a.zorder === b.zorder &&
  a.visible === b.visible &&
  [...a.categoryIds].sort().join() === [...b.categoryIds].sort().join()

export interface GeofenceEditorProps {
  companyId: number
  companyName: string
  /** The geofence being edited; none for a new one. */
  geofence?: GeofenceDetail
  /** The company's geofences: names must be unique among them, and they are drawn for context. */
  geofences: ManagedGeofence[]
  categories: GeofenceCategory[]
  /** Throw away the edits and load the geofence again (after a stale-version refusal). */
  onReload: () => void
}

/**
 * Create or edit one geofence: the form on the left, the drawing map on the right. Save stays off
 * until the name and the shape are both valid; leaving with unsaved changes asks first. Built
 * against the final write contract, so while the writes are placeholders a save says so and keeps
 * everything as it was.
 */
export const GeofenceEditor = ({
  companyId,
  companyName,
  geofence,
  geofences,
  categories,
  onReload,
}: GeofenceEditorProps) => {
  const navigate = useNavigate()
  const { api } = useApi()
  const invalidate = useInvalidateGeofences()
  const { toast, show, showToast, hideToast } = useToast()

  const initialValues = useMemo<GeofenceFormValues>(
    () =>
      geofence
        ? { name: geofence.name, categoryIds: geofence.categoryIds, zorder: geofence.zorder, visible: geofence.visible }
        : DEFAULT_VALUES,
    [geofence]
  )
  const initialShape = useMemo(
    () => (geofence ? shapeFromPoints(geofence.points, shapeTypeOf(geofence.complexityTypeId)) : null),
    [geofence]
  )

  const [values, setValues] = useState(initialValues)
  const [shape, setShape] = useState<GeofenceShape | null>(initialShape)
  const [history, setHistory] = useState<(GeofenceShape | null)[]>([])
  const [tool, setTool] = useState<ShapeType>(initialShape?.type ?? 'polygon')
  const [pendingTool, setPendingTool] = useState<ShapeType>()
  const [resetToken, setResetToken] = useState(0)
  const [nameTouched, setNameTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedGeofenceId, setSavedGeofenceId] = useState<number>()
  const [conflict, setConflict] = useState(false)
  const [search, setSearch] = useState('')
  const [place, setPlace] = useState<{ latitude: number; longitude: number }>()

  const currentGeofenceId = geofence?.id ?? savedGeofenceId
  const otherGeofences = useMemo(
    () => geofences.filter((g) => g.id !== currentGeofenceId),
    [currentGeofenceId, geofences]
  )
  const nameError = validateGeofenceName(
    values.name,
    otherGeofences.map((g) => g.name)
  )
  const shapeError = validateShape(shape)
  const problems = [nameError, shapeError].filter((p): p is string => !!p)
  const dirty = !sameValues(values, initialValues) || shape !== initialShape
  const canSave = dirty && problems.length === 0 && !saving

  // Leaving with unsaved changes asks first; a successful save leaves without asking.
  const leavingRef = useRef(false)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && !leavingRef.current && currentLocation.pathname !== nextLocation.pathname
  )
  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  const backTo = geofence ? `/geofences/${geofence.id}` : '/geofences'
  const leave = useCallback(
    (pathname: string, state?: unknown) => navigate({ pathname, search: `companyId=${companyId}` }, { state }),
    [navigate, companyId]
  )

  const changeShape = useCallback(
    (next: GeofenceShape | null) => {
      setHistory((prev) => [...prev, shape])
      setShape(next)
    },
    [shape]
  )

  const clearShape = () => {
    if (shape) changeShape(null)
    setResetToken((t) => t + 1)
  }

  const undo = () => {
    const previous = history.at(-1)
    if (previous === undefined) return
    setHistory((prev) => prev.slice(0, -1))
    setShape(previous)
    if (previous) setTool(previous.type)
  }

  const chooseTool = (next: ShapeType) => {
    if (next === tool) return
    if (shape) {
      setPendingTool(next)
    } else {
      setTool(next)
    }
  }

  const reportFailure = (failure: WriteFailure, what: string) => {
    if (failure.kind === 'conflict') {
      setConflict(true)
      return
    }
    showToast(
      failure.kind === 'unavailable'
        ? {
            header: 'Not available yet',
            body: `Saving ${what} isn’t available in Insights yet, so nothing was saved. Your changes are still here.`,
            position: 'end',
            info: true,
          }
        : { header: 'Not saved', body: failure.message, position: 'end', warning: true }
    )
  }

  const save = async () => {
    setNameTouched(true)
    if (!shape || problems.length > 0) return
    const body = {
      ...values,
      name: values.name.trim(),
      complexityTypeId: COMPLEXITY_TYPE_IDS[shape.type],
      points: pointsFromShape(shape),
    }
    setSaving(true)
    try {
      const saved = geofence
        ? await updateGeofence(api, geofence.id, geofence.version, body)
        : await createGeofence(api, companyId, body)
      if (!geofence) setSavedGeofenceId(saved.id)
      await invalidate()
      leavingRef.current = true
      leave(`/geofences/${saved.id}`, { savedGeofence: saved.name })
    } catch (ex) {
      reportFailure(writeFailure(ex), 'geofences')
    } finally {
      setSaving(false)
    }
  }

  const area = shape ? areaSquareMeters(shape.ring) : 0

  return (
    <div className="flex h-full min-h-0 flex-col p-4">
      <Link
        to={{
          pathname: geofence ? `/geofences/${geofence.id}` : '/geofences',
          search: `companyId=${companyId}`,
        }}
        className="link link-hover mb-2 inline-block text-sm"
      >
        &larr;Geofences
      </Link>
      <GeofencePageHeader
        title={geofence ? 'Edit ' + geofence.name : 'New geofence'}
        subtitle={companyName}
        actions={
          <>
            <Button variation="ghost" size="sm" handleClick={() => leave(backTo)}>
              Cancel
            </Button>

            <Button
              variation="ghost"
              size="sm"
              icon={<FiCheck />}
              isDisabled={!canSave}
              handleClick={() => void save()}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
      />

      <div className="mt-4 flex min-h-0 flex-1 overflow-hidden rounded-box border border-base-300 bg-base-100">
        <aside className="flex w-80 shrink-0 flex-col gap-5 overflow-y-auto border-r border-base-300 p-4">
          <GeofenceForm
            companyName={companyName}
            values={values}
            onChange={setValues}
            nameError={nameTouched ? nameError : undefined}
            onNameBlur={() => setNameTouched(true)}
            categories={categories}
          />
          {problems.length > 0 && (dirty || nameTouched) && (
            <div className="rounded-box bg-base-200 p-3 text-sm" data-testid="save-checklist">
              <p className="mb-1 font-semibold">Before saving</p>
              <ul className="list-disc pl-5 text-base-content/70">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}
        </aside>

        <section className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-3 border-b border-base-300 px-3 py-2">
            <ShapeToolbar
              tool={tool}
              onToolChange={chooseTool}
              canUndo={history.length > 0}
              onUndo={undo}
              canClear={!!shape}
              onClear={clearShape}
            />
            <div className="ml-auto w-72">
              <AddressAutocomplete
                variant="search"
                label="Search for an address"
                placeholder="Search for an address…"
                value={search}
                coordinates={{ latitude: null, longitude: null }}
                onChange={setSearch}
                onSelect={(found) => {
                  setSearch(found.address)
                  setPlace({ latitude: found.latitude, longitude: found.longitude })
                }}
              />
            </div>
          </div>
          <div className="flex items-center gap-2 bg-info/10 px-3 py-1.5 text-xs" role="status">
            <FiInfo className="shrink-0 text-info" />
            {shape ? EDIT_TIPS[shape.type] : DRAW_TIPS[tool]}
          </div>
          <div className="relative min-h-0 flex-1">
            <GeofenceDrawMap
              shape={shape}
              tool={tool}
              onChange={changeShape}
              zorder={values.zorder}
              context={[]}
              place={place}
              resetToken={resetToken}
            />
          </div>
          <div className="flex items-center gap-3 border-t border-base-300 px-3 py-1.5 text-xs text-base-content/70">
            {shape ? (
              <>
                <span>
                  {SHAPE_LABELS[shape.type]} · {ringVertices(shape.ring).length} points · ≈ {formatArea(area)}
                </span>
                {isLargeShape(shape) && (
                  <span className="flex items-center gap-1 text-warning">
                    <FiAlertTriangle /> This geofence is very large. Check it was drawn as intended.
                  </span>
                )}
              </>
            ) : (
              <span>Nothing drawn yet</span>
            )}
          </div>
        </section>
      </div>

      <ConfirmModal
        show={!!pendingTool}
        title="Replace the shape?"
        body={`Drawing a ${pendingTool ? SHAPE_LABELS[pendingTool].toLowerCase() : ''} removes the current shape. You can undo this.`}
        confirmLabel="Replace"
        onConfirm={() => {
          if (pendingTool) {
            clearShape()
            setTool(pendingTool)
          }
          setPendingTool(undefined)
        }}
        onCancel={() => setPendingTool(undefined)}
      />
      <ConfirmModal
        show={blocker.state === 'blocked'}
        title="Discard changes?"
        body="This geofence has changes that haven’t been saved."
        confirmLabel="Discard"
        variation="danger"
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
      <Modal
        show={conflict}
        onHide={() => setConflict(false)}
        title="Geofence changed"
        variation="warning"
        warning
        bodyContent={
          <p>
            Someone changed this geofence since you opened it. Reload to see their version (your changes will be lost),
            or keep editing.
          </p>
        }
        footerButtons={[
          {
            label: 'Reload',
            useModalVariant: true,
            onClick: () => {
              setConflict(false)
              leavingRef.current = true
              onReload()
            },
          },
          { label: 'Keep editing', onClick: () => setConflict(false) },
        ]}
      />
      <Toast {...toast} show={show} onClose={hideToast} />
    </div>
  )
}
